import { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@firefly/fly-http";
import {addEventListener} from "@firefly/fly-hook";
import PaymentClientContext from "./context/PaymentClientContext";
import executePayment from "./executePayment";
import { getStoragePayments, saveStoragePayments } from "./storage";
import {
  PaymentExecutionOptions,
  PaymentExecutionState,
  PaymentMethod,
  Payment,
  SuspensePayment,
} from "./typing";

// const pendingPaymentOrderId = new URLSearchParams(window.location.search).get("paymentOrderId");

export interface UsePaymentType {
  state: any;
  isPending: boolean | undefined,
  setPending: (value: boolean) => void;
  isCompleted: boolean;
  suspensePaymentOrder: (value?: Payment) => void;
  confirmPaymentOrder: any;
  isCheckFetching: boolean;
  check: () => Promise<void>;
  execute: (order: Payment, method: PaymentMethod, args: Record<string, any>, options?: PaymentExecutionOptions) => Promise<PaymentExecutionState>;
  pendingPayment: SuspensePayment | null;
}
function usePayment(keys: any[] = []): UsePaymentType {
  const [pendingPayment, _setPendingPayment] = useState<SuspensePayment | null>(
    null,
  );
  const paymentIdentifier = JSON.stringify(keys);
  const { defaultOptions: defaultOptionsFn } = useContext(PaymentClientContext);
  useEffect(() => {
    function pullPending() {
      const payments: SuspensePayment[] = getStoragePayments();
      const payment = payments.find((item) => {
        return item.identifier === paymentIdentifier;
      });
      if (payment) {
        _setPendingPayment(payment);
        saveStoragePayments(payments.filter((item) => item !== payment));
      }
    }
    pullPending();
    const listener = addEventListener(document, "visibilitychange", () => {
      const state = document.visibilityState;
      if (state === "visible") {
        //更新状态
        pullPending();
      }
    });
    return () => {
      listener.remove();
    };
  }, [paymentIdentifier]);

  function confirmPaymentOrder(value: Payment) {
    _setPendingPayment({
      identifier: paymentIdentifier,
      id: value.id,
      outId: value.outId,
    });
  }
  /**
   * 挂起支付订单，下次回调回来进行确认 是否支付
   * @param value
   * 部分浏览器快速更新url 后再跳转，不生效，用session进行存储
   */
  function suspensePaymentOrder(value?: Payment) {
    _setPendingPayment(null);

    const storagePayments = getStoragePayments();
    const payments = storagePayments.filter((item) => {
      return item.identifier !== paymentIdentifier;
    });

    if (value) {
      payments.unshift({
        identifier: paymentIdentifier,
        id: value.id,
        outId: value.outId,
      });
    }
    saveStoragePayments(payments);
  }

  const [confirm, setConfirm] = useState<boolean>();
  useEffect(() => {
    if (pendingPayment) {
      setConfirm(true);
    }
  }, [pendingPayment]);

  const {
    isFetching: isCheckFetching,
    refetch: check,
    data,
    // status,
  } = useQuery(
      {
        queryKey: ["payment-order", pendingPayment?.id, "state"],
        queryFn: () => {
          return http.get(`/api/checkout/orders/${pendingPayment?.id}/state`).then(
              (data) => {
                return data;
              },
          );
        },
        //恢复后主动查询等待的订单
        enabled: !!pendingPayment?.id,
      }

  );
  const state = data?.state;
  const isCompleted = state === "COMPLETED";

  function execute(
    order: Payment,
    method: PaymentMethod,
    args: Record<string, any>,
    options: PaymentExecutionOptions = {},
  ): Promise<PaymentExecutionState> {
    const paymentOrderId = order.id;
    const defaultOptions = defaultOptionsFn(method);
    options = Object.assign(defaultOptions, options);

    return executePayment(paymentOrderId, method, args, options).then(
      (status: PaymentExecutionState) => {
        if (status === "suspend") {
          suspensePaymentOrder(order);
        }
        if (status === "confirm") {
          confirmPaymentOrder(order);
        }
        return status;
      },
    );
  }
  return {
    state: state,
    isPending: (confirm && !isCompleted),
    setPending: setConfirm,
    isCompleted: isCompleted,
    suspensePaymentOrder,
    confirmPaymentOrder,
    isCheckFetching,
    check: () => {
      return check().then()
    },
    execute: execute,
    pendingPayment,
  };
}


export default usePayment;
